import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type GradePipelineStep = {
  id: string;
  label: string;
  short: string;
};

type GradePipelineStepsProps = {
  activeIndex: number;
  className?: string;
};

export function GradePipelineSteps({
  activeIndex,
  className = '',
}: GradePipelineStepsProps): ReactNode {
  const { t, i18n } = useTranslation();
  const steps = useMemo((): GradePipelineStep[] => {
    return [
      {
        id: 'prepare',
        label: t('gradeSync.pipeline.prepare.label'),
        short: t('gradeSync.pipeline.prepare.short'),
      },
      {
        id: 'scan',
        label: t('gradeSync.pipeline.scan.label'),
        short: t('gradeSync.pipeline.scan.short'),
      },
      {
        id: 'review',
        label: t('gradeSync.pipeline.review.label'),
        short: t('gradeSync.pipeline.review.short'),
      },
      {
        id: 'publish',
        label: t('gradeSync.pipeline.publish.label'),
        short: t('gradeSync.pipeline.publish.short'),
      },
      {
        id: 'done',
        label: t('gradeSync.pipeline.done.label'),
        short: t('gradeSync.pipeline.done.short'),
      },
    ];
  }, [t, i18n.language]);

  return (
    <div
      className={`dg-surface-card p-3 ${className}`.trim()}
      role="list"
      aria-label={t('gradeSync.pipeline.ariaLabel')}
    >
      <div className="flex flex-wrap items-center justify-between gap-1 sm:gap-0 sm:justify-between">
        {steps.map((step, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <div
              key={step.id}
              className="flex min-w-0 flex-1 items-center sm:flex-initial sm:flex-1"
            >
              <div
                role="listitem"
                className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 px-0.5 text-center sm:px-1 ${
                  isActive ? 'opacity-100' : isPast ? 'opacity-80' : 'opacity-45'
                }`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-black ${
                    isActive
                      ? 'bg-dg-accent-grade text-slate-950 shadow-md ring-2 ring-amber-400/40'
                      : isPast
                        ? 'bg-dg-semantic-success/15 text-emerald-800 dark:text-emerald-200'
                        : 'bg-dg-bg-inset text-dg-fg-muted dark:bg-slate-700 dark:text-slate-400'
                  }`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {i + 1}
                </div>
                <span className="hidden text-[9px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 sm:block">
                  {step.label}
                </span>
                <span className="text-[8px] font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 sm:hidden">
                  {step.short}
                </span>
              </div>
              {i < steps.length - 1 ? (
                <div
                  className={`mx-0.5 h-0.5 min-w-[6px] flex-1 rounded-full sm:mx-1 ${
                    isPast ? 'bg-emerald-500/50' : 'bg-slate-200 dark:bg-slate-600'
                  }`}
                  aria-hidden
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
